<template>
    <!-- BaseAppNavigation.vue -->

    <!-- Secondary Drawer -->
    <v-navigation-drawer
        :color="drawerColor"
        :mini-variant="mini"
        :mini-variant-width="miniVariantWidth"
        :temporary="!mini && $vuetify.breakpoint.smAndDown"
        :width="drawerWidth"
        app
        clipped
        permanent
    >
        <v-row class="fill-height" no-gutters>
            <!-- Primary Drawer -->
            <v-navigation-drawer
                :color="color"
                :mini-variant-width="miniVariantWidth"
                :value="!mini"
                dark
                mini-variant
                permanent
            >
                <!-- Parents / Mini Icons -->
                <v-list dense nav>

                    <!-- Sidebar toggle -->
                    <v-tooltip v-if="hasItemsWithChildren" right>
                        <template v-slot:activator="{ on, attrs }">
                            <v-list-item
                                no-action
                                v-bind="attrs"
                                @click="mini = !mini"
                                v-on="on"
                            >
                                <v-list-item-action>
                                    <v-icon>mdi-menu</v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title></v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <span>{{ mini ? 'Show' : 'Hide' }} Sidebar</span>
                    </v-tooltip>

                    <v-divider></v-divider>

                    <!-- Nav Parent Items -->
                    <v-tooltip v-for="item in items" :key="item.title" color="primary" right>
                        <template v-slot:activator="{ on, attrs }">
                            <v-list-item
                                no-action
                                v-bind="{...item, ...attrs}"
                                v-on="on"
                                @click.stop="() => handleSection(item)"
                            >
                                <v-list-item-action>
                                    <v-icon>{{ item.icon }}</v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <span>{{ item.title }}</span>
                    </v-tooltip>
                </v-list>
            </v-navigation-drawer>

            <!-- Child Items / Secondary Drawer Content-->
            <v-list v-if="!mini" class="grow">

                <!-- Secondary Drawer Subtitle-->
                <template v-if="subtitle">
                    <v-list-item>
                        <v-list-item-title class="primary--text">{{ subtitle }}</v-list-item-title>
                        <v-list-item-action>
                            <v-tooltip right>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon v-bind="attrs" @click="resetDrawer" v-on="on">
                                        <v-icon color="primary">mdi-arrow-left-circle</v-icon>
                                    </v-btn>
                                </template>
                                <span>Hide Sidebar</span>
                            </v-tooltip>
                        </v-list-item-action>
                    </v-list-item>
                    <v-divider></v-divider>
                </template>

                <!-- Secondary Drawer Items -->
                <v-list-item
                    v-for="child in children"
                    :key="child.icon"
                    link
                    v-bind="child"
                    @click="$vuetify.breakpoint.smAndDown && resetDrawer()"
                >
                    <v-list-item-action>
                        <v-icon color="primary">{{ child.icon }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ child.title }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-row>
    </v-navigation-drawer>
</template>

<script>
    import {findIndex, isEmpty, some} from 'underscore'

    export default {
        name: 'BaseAppNavigation',
        data: () => ({
            mini: true,
            subtitle: '',
            children: [],
            lastItem: {}
        }),
        props: {
            /**
             * The color of the nav drawer
             */
            color: {
                type: String,
                default: 'secondary'
            },
            /**
             * Set to false if using a light color
             */
            dark: {
                type: Boolean,
                default: true
            },
            /**
             * The color of the secondary drawer
             */
            drawerColor: {
                type: String,
                default: 'light'
            },
            /**
             * The color of the secondary drawer
             */
            drawerWidth: {
                type: [Number, String],
                default: 320
            },
            /**
             * The width of the drawer when collapsed
             */
            miniVariantWidth: {
                type: [Number, String],
                default: 70
            },
            /**
             * An array of navigation items i.e. [{icon: ..., title: ..., to: {...}, exact: false}]
             */
            items: {
                type: Array,
                default: () => []
            }
        },
        computed: {
            hasItemsWithChildren () {
                return some(this.items, item => !isEmpty(item.children))
            }
        },
        methods: {
            checkMatchedRoutes (matched) {
                const vm = this

                if (!isEmpty(matched)) {
                    // Find match for current route in nav items
                    const index = findIndex(vm.items, parent => {
                        if (!parent.to) {
                            return false
                        }

                        const parentRoute = vm.$router.resolve(parent.to)

                        return some(matched, ({name, href, regex}) => {
                            // Check parent route
                            let check = regex.test(parentRoute.href)

                            if (!check && parent.children) {
                                // Check child routes
                                check = some(parent.children, child => {
                                    if (child.href) {
                                        return false
                                    }

                                    const childRoute = vm.$router.resolve(child.to)

                                    return regex.test(childRoute.href)
                                })
                            }

                            return check
                        })
                    })

                    if (index !== -1) {
                        const item = vm.items[index]
                        vm.subtitle = item.meta && item.meta.subtitle
                        vm.children = item.children
                    }
                }
            },
            handleSection (item) {
                if (item.href || isEmpty(item.children)) {
                    return
                }

                const vm = this
                const isSameSection = item.title === vm.lastItem.title

                if ((!vm.mini && !isSameSection) || vm.mini) {
                    vm.lastItem = item
                    return vm.mini = false
                }

                vm.resetDrawer()
            },
            resetDrawer () {
                this.mini = true
            }
        },
        watch: {
            '$route.matched' (matched) {
                this.checkMatchedRoutes(matched)
            },
            '$vuetify.breakpoint.smAndDown' (newVal) {
                const vm = this
                const listener = () => vm.mini = !vm.mini
                const getEl = () => document.querySelector('.v-overlay.v-overlay--active')
                let el

                if (newVal) {
                    const intervalId = setInterval(() => {
                        if ((el = getEl())) {
                            el.addEventListener('click', listener)
                            clearInterval(intervalId)
                            vm.resetDrawer()
                        }
                    }, 100)
                }
            },
            mini (isMini) {
                const vm = this
                const listener = () => vm.mini = !vm.mini
                const getEl = () => document.querySelector('.v-overlay.v-overlay--active')
                let el

                if (isMini) {
                    return (el = getEl()) && el.removeEventListener('click', listener)
                }

                vm.checkMatchedRoutes(vm.$route.matched)

                if (vm.$vuetify.breakpoint.smAndDown) {
                    const intervalId = setInterval(() => {
                        if ((el = getEl())) {
                            el.addEventListener('click', listener)
                            clearInterval(intervalId)
                        }
                    }, 100)
                }
            }
        },
        created () {
            const vm = this
            vm.checkMatchedRoutes(vm.$route.matched)
        }
    }
</script>
